'use client';

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Progress } from '../../components/ui/progress';
import {
  Loader2,
  User,
  FileText,
  CheckCircle,
  Upload,
  X,
  Camera,
  AlertCircle,
} from 'lucide-react';
import {
  useGetUserById,
  useUpdateUserProfile,
  useSubmitRegistration,
  useCheckUsername,
} from '../../hooks/use-user';
import { useCheckUserLogin } from '../../hooks/use-user';
import { useUploadProfileImage, useDeleteFile } from '../../hooks/use-upload';
import { useAuthStore, selectIsAuthenticated, usePassportSSO } from '../../hooks';
import { Navbar } from '../../components/layout/navbar';
import { toast } from 'sonner';

export default function CompleteRegistrationPage() {
  const navigate = useNavigate();

  // Use auth store for Passport SSO authentication
  const { isCheckingSession } = usePassportSSO();
  const isStoreAuthenticated = useAuthStore(selectIsAuthenticated);
  const storeUser = useAuthStore((state) => state.user);
  const isLoading = useAuthStore((state) => state.isLoading);

  // Get user identifier - wallet address or passportId
  const userIdentifier = storeUser?.wallet || storeUser?.creator || storeUser?.passportId;

  const [username, setUsername] = useState('');
  const [debouncedUsername, setDebouncedUsername] = useState('');
  const [bio, setBio] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Avatar upload state
  const [avatar, setAvatar] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedAvatarUrl, setUploadedAvatarUrl] = useState(null);
  const avatarInputRef = useRef(null);

  // Fetch user profile - use passportId or wallet address
  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
  } = useGetUserById(userIdentifier);

  const {
    data: isLoggedIn,
    isLoading: isLoadingLogin,
    isError,
  } = useCheckUserLogin();

  // Check username availability (debounced)
  const { data: usernameCheck, isLoading: isCheckingUsername } =
    useCheckUsername(debouncedUsername);

  // Debounce username for availability check
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only check if username is different from current user's username
      if (username && username !== user?.username) {
        setDebouncedUsername(username);
      } else {
        setDebouncedUsername('');
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [username, user?.username]);

  // Upload hooks
  const uploadProfileImage = useUploadProfileImage((progress) =>
    setUploadProgress(progress)
  );
  const deleteFile = useDeleteFile();

  // Submit registration mutation
  const submitRegistration = useSubmitRegistration(userIdentifier, {
    onSuccess: () => {
      toast.success('Registration completed successfully!');
      window.location.reload();
    },
    onError: (error) => {
      // Check if error is due to duplicate username
      if (
        error?.response?.data?.code === 'DUPLICATE_USERNAME' ||
        error?.response?.status === 409 ||
        error?.response?.data?.error?.toLowerCase()?.includes('username')
      ) {
        toast.error(
          'Username is already taken. Please choose a different one.'
        );
        return;
      }
      // If submission fails, clean up the uploaded avatar
      if (uploadedAvatarUrl) {
        const fileKey = uploadedAvatarUrl.replace(
          'https://media.tracverse.com/',
          ''
        );
        deleteFile.mutate(fileKey);
      }
      toast.error('Failed to complete registration. Please try again.');
      console.error('Registration error:', error);
    },
  });

  // Redirect if user is already verified or not logged in
  useEffect(() => {
    // Wait for session check to complete
    if (isCheckingSession || isLoading) return;

    // Not authenticated via Passport SSO - redirect to sign up
    if (!isStoreAuthenticated || !storeUser) {
      navigate('/sign-up');
      return;
    }

    // Already verified - redirect to profile
    if (user && user.isVerified) {
      navigate('/profile/' + userIdentifier);
      return;
    }
  }, [user, isStoreAuthenticated, storeUser, userIdentifier, navigate, isCheckingSession, isLoading]);

  // Pre-fill form if user has existing data
  useEffect(() => {
    if (user) {
      setUsername(user.username || '');
      setBio(user.bio || '');
      setAvatar(user.avatar || '');
      setAvatarPreview(user.avatar || '');
    }
  }, [user]);

  // Cleanup uploaded avatar on unmount if not saved
  useEffect(() => {
    return () => {
      if (uploadedAvatarUrl) {
        const fileKey = uploadedAvatarUrl.replace(
          'https://media.tracverse.com/',
          ''
        );
        deleteFile.mutate(fileKey);
      }
    };
  }, [uploadedAvatarUrl]);

  // Handle avatar upload
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];

    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }

      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setAvatarPreview(previewUrl);
      setIsUploading(true);

      try {
        const avatarFormData = new FormData();
        avatarFormData.append('file', file);
        const response = await uploadProfileImage.mutateAsync(avatarFormData);

        if (response.success && response.url) {
          // If we had a previously uploaded avatar in this session, delete it
          if (uploadedAvatarUrl) {
            const oldFileKey = uploadedAvatarUrl.replace(
              'https://media.tracverse.com/',
              ''
            );
            try {
              await deleteFile.mutateAsync(oldFileKey);
            } catch (error) {
              // Silently continue
            }
          }
          setUploadedAvatarUrl(response.url);
          setAvatar(response.url);
          toast.success('Avatar uploaded successfully');
        } else {
          toast.error('Upload failed - no URL returned');
          setAvatarPreview(user?.avatar || '');
        }
      } catch (error) {
        toast.error(
          `Failed to upload avatar: ${
            error.response?.data?.error || error.message || 'Unknown error'
          }`
        );
        setAvatarPreview(user?.avatar || '');
        URL.revokeObjectURL(previewUrl);
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    }
  };

  // Handle avatar removal
  const handleRemoveAvatar = async () => {
    // If we uploaded a new image in this session, delete it from S3
    if (uploadedAvatarUrl) {
      const fileKey = uploadedAvatarUrl.replace(
        'https://media.tracverse.com/',
        ''
      );
      try {
        await deleteFile.mutateAsync(fileKey);
      } catch (error) {
        // Silently continue
      }
      setUploadedAvatarUrl(null);
    }
    setAvatar('');
    setAvatarPreview('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Sanitize inputs
    const sanitizedUsername = DOMPurify.sanitize(username.trim(), {
      ALLOWED_TAGS: [],
    });
    const sanitizedBio = DOMPurify.sanitize(bio.trim(), { ALLOWED_TAGS: [] });

    if (!sanitizedUsername) {
      toast.error('Username is required');
      return;
    }

    if (!sanitizedBio) {
      toast.error('Bio is required');
      return;
    }

    if (sanitizedUsername.length < 3) {
      toast.error('Username must be at least 3 characters long');
      return;
    }

    if (sanitizedBio.length < 10) {
      toast.error('Bio must be at least 10 characters long');
      return;
    }

    // Check if username is available (only if it's different from current username)
    if (
      sanitizedUsername !== user?.username &&
      usernameCheck?.checked &&
      !usernameCheck?.available
    ) {
      toast.error('Username is already taken. Please choose a different one.');
      return;
    }

    setIsSubmitting(true);

    try {
      const registrationData = {
        username: sanitizedUsername,
        bio: sanitizedBio,
      };

      // Include avatar if uploaded
      if (avatar) {
        registrationData.avatar = avatar;
      }

      await submitRegistration.mutateAsync(registrationData);
      // Clear the uploaded URL ref since it's now saved successfully
      setUploadedAvatarUrl(null);
    } catch (error) {
      console.error('Error completing registration:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading while checking session
  if (isCheckingSession || isLoading) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex items-center gap-2 text-white">
            <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
            <span>Checking authentication...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!isStoreAuthenticated || !storeUser) {
    return null; // Will redirect
  }

  if (isLoadingUser) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex items-center gap-2 text-white">
            <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
            <span>Loading your profile...</span>
          </div>
        </div>
      </div>
    );
  }

  // 404 error means user doesn't exist yet - that's expected for new registrations
  // Only show error for non-404 errors
  if (userError && userError?.response?.status !== 404) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar />
        <div className="container mx-auto px-4 py-12">
          <Alert className="max-w-md mx-auto bg-red-900/20 border-red-900/50 text-red-200">
            <AlertDescription>
              Error loading your profile. Please try refreshing the page.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Gradient Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
      </div>

      <Navbar />

      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="h-16 w-16 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/20">
              <User className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-white to-zinc-400 bg-clip-text text-transparent">
              Complete Your Registration
            </h1>
            <p className="text-zinc-400">
              Just a few more details to unlock all features and start creating
              content
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-green-900/30 border border-green-500/30 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <span className="text-sm font-medium text-green-500">
                  Wallet Connected
                </span>
              </div>
              <div className="h-px w-12 bg-zinc-800"></div>
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-purple-900/30 border border-purple-500/30 flex items-center justify-center">
                  <span className="text-sm font-medium text-purple-400">2</span>
                </div>
                <span className="text-sm font-medium text-purple-400">
                  Complete Profile
                </span>
              </div>
            </div>
          </div>

          {/* Registration Form */}
          <Card className="border border-white/10 bg-zinc-900/50 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-white">Profile Information</CardTitle>
              <CardDescription className="text-zinc-400">
                This information will be visible to other users and helps build
                trust in the community
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label
                    htmlFor="username"
                    className="text-sm font-medium text-zinc-300"
                  >
                    Username *
                  </Label>
                  <div className="relative">
                    <Input
                      id="username"
                      type="text"
                      placeholder="Enter your username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className={`w-full bg-black border-white/10 text-white placeholder:text-zinc-600 focus:border-purple-500 pr-10 ${
                        usernameCheck?.checked &&
                        !usernameCheck?.available &&
                        username !== user?.username
                          ? 'border-red-500 focus:border-red-500'
                          : usernameCheck?.checked && usernameCheck?.available
                          ? 'border-green-500 focus:border-green-500'
                          : ''
                      }`}
                      maxLength={50}
                      required
                    />
                    {/* Username status indicator */}
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {isCheckingUsername && username.length >= 3 && (
                        <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
                      )}
                      {!isCheckingUsername &&
                        usernameCheck?.checked &&
                        usernameCheck?.available && (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        )}
                      {!isCheckingUsername &&
                        usernameCheck?.checked &&
                        !usernameCheck?.available &&
                        username !== user?.username && (
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        )}
                    </div>
                  </div>
                  {/* Username feedback message */}
                  {usernameCheck?.checked &&
                  !usernameCheck?.available &&
                  username !== user?.username ? (
                    <p className="text-xs text-red-400 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Username is already taken
                    </p>
                  ) : usernameCheck?.checked && usernameCheck?.available ? (
                    <p className="text-xs text-green-400 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Username is available
                    </p>
                  ) : (
                    <p className="text-xs text-zinc-500">
                      Choose a unique username that represents you (3-50
                      characters)
                    </p>
                  )}
                </div>

                {/* Avatar Upload */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-zinc-300">
                    Profile Picture (Optional)
                  </Label>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      {avatarPreview ? (
                        <div className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-white/10">
                          <img
                            src={avatarPreview}
                            alt="Avatar preview"
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                          {isUploading && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                              <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
                            </div>
                          )}
                          {!isUploading && (
                            <button
                              type="button"
                              onClick={handleRemoveAvatar}
                              className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600 transition-colors"
                            >
                              <X className="h-4 w-4 text-white" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => avatarInputRef.current?.click()}
                          disabled={isUploading}
                          className="w-24 h-24 rounded-full border-2 border-dashed border-white/20 flex flex-col items-center justify-center gap-1 hover:border-purple-500/50 hover:bg-purple-500/5 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Camera className="h-6 w-6 text-zinc-500" />
                          <span className="text-xs text-zinc-500">Upload</span>
                        </button>
                      )}
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                    </div>
                    <div className="flex-1">
                      {avatarPreview ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => avatarInputRef.current?.click()}
                          disabled={isUploading}
                          className="border-white/10 text-zinc-300 hover:text-white hover:bg-white/5 bg-transparent"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Change Photo
                        </Button>
                      ) : (
                        <p className="text-xs text-zinc-500">
                          Upload a profile picture to personalize your account.
                          Max 5MB. Supports JPG, PNG, GIF, WebP.
                        </p>
                      )}
                      {isUploading && uploadProgress > 0 && (
                        <div className="mt-2">
                          <Progress value={uploadProgress} className="h-1" />
                          <p className="text-xs text-zinc-500 mt-1">
                            Uploading... {Math.round(uploadProgress)}%
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="bio"
                    className="text-sm font-medium text-zinc-300"
                  >
                    Bio *
                  </Label>
                  <Textarea
                    id="bio"
                    placeholder="Tell us about yourself, your interests, and what you'd like to share with the community..."
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    className="w-full min-h-[120px] resize-none bg-black border-white/10 text-white placeholder:text-zinc-600 focus:border-purple-500"
                    maxLength={500}
                    required
                  />
                  <div className="flex justify-between text-xs text-zinc-500">
                    <span>Minimum 10 characters required</span>
                    <span>{bio.length}/500</span>
                  </div>
                </div>

                <Alert className="bg-blue-900/10 border-blue-900/30 text-blue-200">
                  <FileText className="h-4 w-4 text-blue-400" />
                  <AlertDescription>
                    <strong className="text-blue-300">
                      Why do we need this?
                    </strong>{' '}
                    Your username and bio help other users understand who you
                    are and what you bring to the community. This creates a more
                    engaging and trustworthy environment for everyone.
                  </AlertDescription>
                </Alert>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/')}
                    className="flex-1 border-white/10 text-zinc-300 hover:text-white hover:bg-white/5 bg-transparent"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      isUploading ||
                      isCheckingUsername ||
                      !username.trim() ||
                      !bio.trim() ||
                      (usernameCheck?.checked &&
                        !usernameCheck?.available &&
                        username !== user?.username)
                    }
                    className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-0"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Completing...
                      </>
                    ) : isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Uploading...
                      </>
                    ) : (
                      'Complete Registration'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* What's Next */}
          <Card className="mt-8 bg-gradient-to-r from-purple-900/10 to-pink-900/10 border border-purple-500/20">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-3 text-purple-300">
                What's next?
              </h3>
              <div className="space-y-2 text-sm text-purple-200/80">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-purple-400" />
                  <span>Write and publish articles</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-purple-400" />
                  <span>Participate in forum discussions</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-purple-400" />
                  <span>Connect with other community members</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-purple-400" />
                  <span>Build your reputation and following</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
